/**
 * Keep the newest line in view — the React half of design §17.6.9 (FR-SC-1…3, FR-SC-5, unit WC-10).
 *
 * THE ROOT CAUSE (investigation-web-channels.md, sub-investigation A, finding 1). The test chat's
 * message area was a valid scroll container, but nothing ever moved it: `onSuccess` replaced the
 * whole chat and the scroll position stayed where it was, so the tester's line, "The agent is
 * answering…" and the reply all landed below the fold. The phone test's transcript pane had the
 * same gap.
 *
 * THE CONTRACT, the same one `packages/web-chat-widget/src/stick-to-bottom.ts` implements in plain DOM:
 *
 *   - "at bottom" is `scrollHeight − scrollTop − clientHeight ≤ one line` (the list's computed
 *     line height). It is a ref, changed only by the scroll handler and by our own scrolls.
 *   - After a render that added a line or grew the last one (a streamed delta), a layout effect
 *     scrolls to the end — but only if the scroller was at the bottom BEFORE that render. Otherwise
 *     the view is left alone and `hasNewLine` turns true, which shows the "New messages" button.
 *   - `jumpToNewest()` scrolls to the end (smoothly unless the reader asked for reduced motion),
 *     clears `hasNewLine`, and calls `onJump` — the panel returns focus to its composer there.
 *   - When the list's box shrinks (the on-screen keyboard, a panel resize) and it was at the
 *     bottom, it is scrolled to the end again.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

export interface StickToBottomInput {
  /** How many lines the scroller shows. A larger count is a new line. */
  readonly lineCount: number;
  /** The newest line's text length. A larger length is the same line still arriving. */
  readonly lastLineLength: number;
  /** Called after `jumpToNewest()` has moved the view — where the panel puts focus back. */
  readonly onJump?: () => void;
}

export interface StickToBottom<T extends HTMLElement> {
  /** A callback ref for the element that scrolls (the chat's `role="log"` list, the transcript pane). */
  readonly bindLog: (node: T | null) => (() => void) | undefined;
  /** A line arrived while the reader was scrolled up: show the "New messages" button. */
  readonly hasNewLine: boolean;
  /** Scroll to the newest line and pin the view there again. */
  readonly jumpToNewest: () => void;
}

/**
 * Only happy-dom (the unit tests) computes neither a line height nor a font size; there the root
 * size of the default 16-unit font at a 1.5 leading stands in. A browser always has both.
 */
const LINE_WHERE_NOTHING_IS_COMPUTED = 24;

/** One line of the list: its computed line height; `normal` is taken as 1.5 × the font size. */
export function lineHeightOf(element: HTMLElement): number {
  const style = globalThis.getComputedStyle(element);
  const lineHeight = Number.parseFloat(style.lineHeight);
  if (Number.isFinite(lineHeight) && lineHeight > 0) return lineHeight;
  const fontSize = Number.parseFloat(style.fontSize);
  if (Number.isFinite(fontSize) && fontSize > 0) return fontSize * 1.5;
  return LINE_WHERE_NOTHING_IS_COMPUTED;
}

/** Whether the scroller shows its last line, give or take one line. */
export function isAtBottom(element: HTMLElement): boolean {
  return element.scrollHeight - element.scrollTop - element.clientHeight <= lineHeightOf(element);
}

function prefersReducedMotion(): boolean {
  return typeof globalThis.matchMedia === "function" && globalThis.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function useStickToBottom<T extends HTMLElement>({
  lineCount,
  lastLineLength,
  onJump,
}: StickToBottomInput): StickToBottom<T> {
  const element = useRef<T | null>(null);
  const [hasNewLine, setHasNewLine] = useState(false);
  const atBottom = useRef(true);
  const seen = useRef({ lineCount, lastLineLength });
  const onJumpRef = useRef(onJump);
  useLayoutEffect(() => {
    onJumpRef.current = onJump;
  }, [onJump]);

  // Attached once per element; React 19 runs the returned cleanup when the element detaches.
  const bindLog = useCallback((node: T | null): (() => void) | undefined => {
    element.current = node;
    if (node === null) return undefined;

    // The reader's own scrolling is the only thing that unpins the view.
    const onScroll = (): void => {
      atBottom.current = isAtBottom(node);
      if (atBottom.current) setHasNewLine(false);
    };
    node.addEventListener("scroll", onScroll, { passive: true });

    // A shorter box (the on-screen keyboard opening, the panel resized) keeps a pinned view pinned.
    let blockSize = node.clientHeight;
    const observer =
      typeof globalThis.ResizeObserver === "function"
        ? new globalThis.ResizeObserver(() => {
            const next = node.clientHeight;
            if (next < blockSize && atBottom.current) node.scrollTop = node.scrollHeight;
            blockSize = next;
          })
        : null;
    observer?.observe(node);

    return () => {
      node.removeEventListener("scroll", onScroll);
      observer?.disconnect();
      element.current = null;
    };
  }, []);

  // After the DOM has the new line and before the browser paints it.
  useLayoutEffect(() => {
    const before = seen.current;
    seen.current = { lineCount, lastLineLength };
    const node = element.current;
    if (node === null) return;
    const grew = lineCount > before.lineCount || (lineCount === before.lineCount && lastLineLength > before.lastLineLength);
    if (atBottom.current) {
      node.scrollTop = node.scrollHeight;
    } else if (grew) {
      setHasNewLine(true);
    }
  }, [lineCount, lastLineLength]);

  const jumpToNewest = useCallback((): void => {
    const node = element.current;
    if (node !== null) {
      node.scrollTo({ top: node.scrollHeight, behavior: prefersReducedMotion() ? "auto" : "smooth" });
    }
    atBottom.current = true;
    setHasNewLine(false);
    onJumpRef.current?.();
  }, []);

  return { bindLog, hasNewLine, jumpToNewest };
}

/** The custom property the phone-width panel sizes itself by (test-console.css, `width < 48em`). */
export const VIEWPORT_BLOCK_SIZE_PROPERTY = "--sandbox-viewport-block-size";

/**
 * Keeps the composer above the on-screen keyboard (FR-SC-6). Mobile Safari ignores the viewport
 * meta's `interactive-widget` and leaves the layout viewport — and so `100dvh` — full height while
 * the keyboard is open; only `visualViewport` shrinks. This writes the visual viewport's bottom
 * edge (its height plus how far it has panned) onto the element as `--sandbox-viewport-block-size`,
 * which the phone-width rule reads. Where there is no `visualViewport` the rule's `100dvh` applies.
 * Returns a callback ref for the panel.
 */
export function useViewportBlockSize<T extends HTMLElement>(): (element: T | null) => void {
  const [target, setTarget] = useState<T | null>(null);
  useEffect(() => {
    const viewport = globalThis.visualViewport;
    if (target === null || viewport === null || viewport === undefined) return undefined;
    const update = (): void => {
      target.style.setProperty(VIEWPORT_BLOCK_SIZE_PROPERTY, `${String(viewport.height + viewport.offsetTop)}px`);
    };
    update();
    viewport.addEventListener("resize", update);
    viewport.addEventListener("scroll", update);
    return () => {
      viewport.removeEventListener("resize", update);
      viewport.removeEventListener("scroll", update);
      target.style.removeProperty(VIEWPORT_BLOCK_SIZE_PROPERTY);
    };
  }, [target]);
  return setTarget;
}
